import { useCallback, useContext, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocumentProxy } from "pdfjs-dist";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton/IconButton";

import type { WidgetConfiguration } from "@/lib/api";
import { darkGrey } from "@/theme/colors";
import useStateFromContext from "@/features/shared/hooks/use-state-from-context";
import {
  WidgetContainerContext,
  WidgetContainerContextProps,
  WidgetContainerHeaderActionsProps,
} from "@/features/dashboard/components/widget-container-card/context";

import { StyledPDFWrapper } from "./inline-pdf.styles";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// prevent settings workerSrc multiple times if multiple widgets are used
if (pdfjs.GlobalWorkerOptions.workerSrc === "pdf.worker.js") {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.js",
    import.meta.url,
  ).toString();
}

interface InlinePDFProps {
  data: {
    url: string;
  };
  configuration: WidgetConfiguration;
}

const InlinePDF = ({ data }: InlinePDFProps) => {
  const [maxPageNumber, setMaxPageNumber] = useState(0);
  const [wrapperWidth, setWrapperWidth] = useState<number | undefined>(undefined);

  const widgetContainerContext = useContext(WidgetContainerContext);
  const [, setHeaderActions] = useStateFromContext<
    WidgetContainerHeaderActionsProps,
    WidgetContainerContextProps
  >("headerActions", widgetContainerContext);

  useEffect(() => {
    if (!data.url || !setHeaderActions) return;

    setHeaderActions((prevState) => {
      if (prevState.pdfDownload) return prevState;

      return {
        ...prevState,
        pdfDownload: (
          <IconButton href={`${import.meta.env.VITE_API_BASE_PATH}${data.url}`}>
            <DownloadIcon fontSize="small" htmlColor={darkGrey} />
          </IconButton>
        ),
      };
    });
  }, [data, setHeaderActions]);

  const onPDFLoad = useCallback(
    (pdf: PDFDocumentProxy) => {
      setMaxPageNumber(pdf.numPages);
    },
    [setMaxPageNumber],
  );

  const handleWrapper = useCallback((node: HTMLDivElement) => {
    if (!node) return undefined;

    setWrapperWidth(node.offsetWidth);
  }, []);

  if (!data?.url) return null;

  return (
    <StyledPDFWrapper ref={handleWrapper}>
      <Document
        file={`${import.meta.env.VITE_API_BASE_PATH}${data.url}`}
        onLoadSuccess={onPDFLoad}
      >
        {[...new Array(maxPageNumber)].map((_, index) => (
          <Page pageNumber={index + 1} key={index} width={wrapperWidth} />
        ))}
      </Document>
    </StyledPDFWrapper>
  );
};

export default InlinePDF;
